import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useRoom } from '../context/RoomContext';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';
import logo1 from '../assets/images/logo1.png';
import visio from '../assets/images/visio.png';
import socketService from '../services/socket';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const { createRoom, joinRoom, isConnected, error, setError } = useRoom();
  const { user, logout, loading } = useAuth();
  const [userName, setUserName] = useState(user?.name || '');
  const [roomName, setRoomName] = useState('');
  const [roomId, setRoomId] = useState('');
  const [activeTab, setActiveTab] = useState('create');
  const [isLoading, setIsLoading] = useState(false);
  const [connectionFailed, setConnectionFailed] = useState(false);

  useEffect(() => {
    const handleTimeout = (e) => {
      setConnectionFailed(true);
      setError(`Serveur inaccessible (${e.detail.url})`);
    };
    const handleError = (e) => {
      if (!socketService.isConnected()) {
        setConnectionFailed(true);
      }
    };
    const handleConnect = () => {
      setConnectionFailed(false);
    };
    window.addEventListener('socket:connection-timeout', handleTimeout);
    window.addEventListener('socket:connection-error', handleError);
    socketService.on('connect', handleConnect);

    return () => {
      window.removeEventListener('socket:connection-timeout', handleTimeout);
      window.removeEventListener('socket:connection-error', handleError);
      socketService.off('connect', handleConnect);
    };
  }, [setError]);

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    if (!userName.trim()) {
      setError('Veuillez entrer votre nom');
      return;
    }

    setIsLoading(true);
    try {
      const room = await createRoom(roomName || 'My Room', userName);
      navigate(`/room/${room.id}`);
    } catch (err) {
      console.error('Erreur lors de la création de la salle:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinRoom = async (e) => {
    e.preventDefault();
    if (!userName.trim()) {
      setError('Veuillez entrer votre nom');
      return;
    }
    if (!roomId.trim()) {
      setError('Please enter a room ID');
      return;
    }

    setIsLoading(true);
    try {
      await socketService.joinWaitingRoom(roomId, userName);
      sessionStorage.setItem(`waitingRoomName_${roomId}`, userName);
      navigate(`/rooms/${roomId}/waiting`);
    } catch (err) {
      console.error('Erreur lors de la connexion à la salle d\'attente:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="home-container">
      {loading ? (
        <div className="user-nav">
          <div className="nav-logo">
            <Link to="/" className="logo-link">
              <img src={logo1} alt="SOLOLA Logo" />
            </Link>
          </div>
          <div className="user-links">
            <Link to="/login">Connexion</Link>
            <Link to="/register" className="register-btn">S'inscrire</Link>
          </div>
        </div>
      ) : user ? (
        <div className="user-nav">
          <div className="nav-logo">
            <Link to="./" className="logo-link">
              <img src={logo1} alt="SOLOLA Logo" />
            </Link>
          </div>
          <div className="user-info">
            <span className="user-name">{user.name}</span>
            <span className="user-role">{user.role}</span>
          </div>
          <div className="user-links">
            <Link to="/profile">Mon compte</Link>
            {user.role === 'Administrateur' && (
              <div className="admin-dropdown">
                <button className="admin-btn">Admin ▾</button>
                <div className="admin-dropdown-content">
                  <Link to="/admin">Dashboard</Link>
                  <Link to="/admin/users">Utilisateurs</Link>
                  <Link to="/admin/statistics">Statistiques</Link>
                  <Link to="/admin/roles">Rôles</Link>
                  <Link to="/admin/settings">Paramètres</Link>
                </div>
              </div>
            )}
            <button onClick={() => { logout(); navigate('/login'); }}>Deconnexion</button>
          </div>
        </div>
      ) : (
        <div className="user-nav">
          <div className="nav-logo">
            <Link to="/" className="logo-link">
              <img src={logo1} alt="SOLOLA Logo" />
            </Link>
          </div>
          <div className="user-links">
            <Link to="/login">Connexion</Link>
            <Link to="/register" className="register-btn">S'inscrire</Link>
          </div>
        </div>
      )}
      
      <div className="home-content">
        <div className="main-section">
          <div className="logo-section">
            <img src={visio} alt="Visio" className="logo-visio" />
            <h1 className="logo">SOLOLA</h1>
            <p className="tagline">Connectez vous avec n'importe qui, n'importe où en utilisant l'application SOLOLA</p>
          </div>

          <div className="connection-status">
            {/*
            <span className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`} />
            {isConnected ? 'Connecter au server' : (connectionFailed ? 'Serveur inaccessible' : 'Connexion...')}
            */}
          </div>

          {error && (
            <div className="error-message">
              {error}
              <button onClick={() => setError(null)}>✕</button>
            </div>
          )}

          <div className="form-container">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'create' ? 'active' : ''}`}
              onClick={() => setActiveTab('create')}
              style={{color: activeTab === 'create' ? '#fff' : '#000'}}
            >
              Créer une réunion
            </button>
            <button
              className={`tab ${activeTab === 'join' ? 'active' : ''}`}
              onClick={() => setActiveTab('join')}
              style={{color: activeTab === 'join' ? '#fff' : '#000'}}
            >
              Réjoindre une réunion
            </button>
          </div>

          <div className="form-content">
            <div className="input-group">
              <label htmlFor="userName">Votre nom</label>
              <input
                id="userName"
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Entrez votre nom"
                disabled={isLoading}
              />
            </div>

            {activeTab === 'create' ? (
              <form onSubmit={handleCreateRoom}>
                <div className="input-group">
                  <label htmlFor="roomName">Nom de la salle (optional)</label>
                  <input
                    id="roomName"
                    type="text"
                    value={roomName}
                    onChange={(e) => setRoomName(e.target.value)}
                    placeholder="Entrez le nom de la salle"
                    disabled={isLoading}
                  />
                </div>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={!isConnected || isLoading}
                >
                  {isLoading ? 'Creating...' : 'Démarrer une réunion'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleJoinRoom}>
                <div className="input-group">
                  <label htmlFor="roomId">Room ID</label>
                  <input
                    id="roomId"
                    type="text"
                    value={roomId}
                    onChange={(e) => setRoomId(e.target.value)}
                    placeholder="Entrez l'ID de l a réunion"
                    disabled={isLoading}
                  />
                </div>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={!isConnected || isLoading}
                >
                  {isLoading ? 'Joining...' : 'Réjoindre'}
                </button>
              </form>
            )}
          </div>
        </div>
        </div>

        <div className="features">
          <div className="feature">
            <span className="feature-icon"></span>
            <span></span>
          </div>
          <div className="feature">
            <span className="feature-icon"></span>
            <span></span>
          </div>
          <div className="feature">
            <span className="feature-icon"></span>
            <span></span>
          </div>
          <div className="feature">
            <span className="feature-icon"></span>
            <span></span>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
    
  );
};

export default Home;